import { select, put, all } from 'redux-saga/effects';
import {
  setPostAcqSelectedOpportunityCensusTracts, appNotify, setPostAcqSelectedProformaInputsContainer, setPostAcqSelectedOpportunityImages,
  setPostAcqUWMarkerState, setPostAcqTransientValuation,
  setPostAcqSelectedValuation, setIsFetchingPostAcqUWImages
} from '../store/actions';
import {
  userSelector,
  selectedPostAcqProformaInputsSelector
} from 'store/selectors';
import { getWithToken, postWithToken } from 'api/network';
import placeholder from 'assets/images/placeholder.png';
import { getGoogleImageUrls, getStreetViewUrl, getUWPropertyImage } from 'utilities/urls';
import { fullAddress } from 'utilities/address';
import { ToCurrencyString } from 'services/formatService';
import _ from 'lodash';

const placeholderSource = {
  source: 'Placeholder',
  images: [{
    id: 1,
    caption: null,
    imageUrl: placeholder,
    source: 'Placeholder',
    sortOrder: 1
  }]
}

export function* getProformaInputsAndValuation(proformaInputsContainer, isUWModal) {
  const [user] = yield all([
    select(userSelector)
  ]);

  const currentUsersValuation = proformaInputsContainer.valuations.find(v => v.user.id === user.id);
  let selectedValuation;
  if (isUWModal) {
    selectedValuation = proformaInputsContainer.valuations[0]; // Only one valuation returned for modal
  } else if (proformaInputsContainer.selectedValuationId) {
    selectedValuation = proformaInputsContainer.valuations.find(v => v.id === proformaInputsContainer.selectedValuationId);
  } else {
    selectedValuation = currentUsersValuation;
  }

  yield put(setPostAcqSelectedProformaInputsContainer(proformaInputsContainer, isUWModal));
  yield put(setPostAcqSelectedValuation(selectedValuation, isUWModal));
  const transientValuation = isUWModal ? selectedValuation : {
    ...currentUsersValuation,
    valuationsComps: [...currentUsersValuation.valuationsComps]
  };
  yield put(setPostAcqTransientValuation(transientValuation, true, isUWModal));
  yield put(setPostAcqUWMarkerState({
    selectedMarker: null,
    isInfoBoxOpen: false,
    isFetchingImages: false,
    selectedMarkerImages: [],
    uncapturedMarkers: []
  }, isUWModal));
}

export function* removeInvalidProformaInputs(transientValuation) {
  const selectedProformaInputs = yield select(selectedPostAcqProformaInputsSelector);
  const inputGroups = {
    rent: {
      name:'rents',
      displayName: 'Rent'
    },
    afterRepairValue: {
      name:'afterRepairValues',
      displayName: 'After Repair Value'
    },
    lotValue: {
      name:'lotValues',
      displayName: 'Lot Value'
    },
    asIsValue: {
      name:'asIsValues',
      displayName: 'As Is Value'
    },
    reducedRent: {
      name:'reducedRents',
      displayName: 'Reduced Rent'
    },
    reducedAfterRepairValue: {
      name:'reducedAfterRepairValues',
      displayName: 'Reduced After Repair Value'
    },
  };

  const compareInputValueToRadioValue = (inputValue, radioValue) => {
    // Both values are allowed to be null
    // If both are null or undefined, then we consider they the same
    if (inputValue == null && radioValue == null) return true;
    // Since all inputs are dollar amoutns, if both have value, we compare the value by rounding them to 0 decimals
    // NOTE: We do this to avoid any discrepancy between saved/calculated decimal numbers 
    if (inputValue != null && radioValue != null) return _.round(inputValue, 0) === _.round(radioValue, 0)
    
    // If one has value whereas the other doesn't, apparently they are different
    return false;
  }

  let invalidFields = '';
  let isFirst = true;
  const updatedTransientValuation = {
    ...transientValuation,
    valuationInput: {
      ...transientValuation.valuationInput
    }
  }
  Object.keys(inputGroups).forEach(group => {
    const inputValue = transientValuation.valuationInput[group];
    const inputValueType = transientValuation.valuationInput[group + 'Type'];
    if (inputValueType === 0 || inputValueType == null) return;

    const proformaOption = selectedProformaInputs[inputGroups[group].name].find(opt => opt.type === inputValueType);
    if (!proformaOption || !compareInputValueToRadioValue(inputValue, proformaOption.value)) {
      updatedTransientValuation.valuationInput[group] = null;
      updatedTransientValuation.valuationInput[group+ 'Type'] = null;
      if (isFirst) {
        invalidFields += '\n\nIncompatible inputs:';
        isFirst = false;
      }
      invalidFields += `\n${inputGroups[group].displayName}, ${inputValue != null ? ToCurrencyString(inputValue) : inputValue}`;
    }
  });
  return { updatedTransientValuation, invalidFields };
}

export function* getPostAcqUWCensusTract(taskId) {
  try {
    const response = yield getWithToken(`/api/postAcquisition/tasks/${taskId}/censusTracts`);      
    const fetchedData = yield response.json();
    yield put(setPostAcqSelectedOpportunityCensusTracts(fetchedData));
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
  }
}

export function* getPostAcqUWImages(taskData) {
  if (!taskData) {
    yield put(setPostAcqSelectedOpportunityImages([placeholderSource]));
    return;
  }

  const getGoogleImageSource = (googleImageUrls) => {
		const images = [];
		if (googleImageUrls.streetViewUrl) {
			images.push({
				id: 1,
				caption: 'Street View',
				imageUrl: googleImageUrls.streetViewUrl,
				source: 'Google',
				sortOrder: 1
			});
		}
		images.push({
			id: images.length,
			caption: 'Satellite View',
			imageUrl: googleImageUrls.mapsStaticUrl,
			source: 'Google',
			sortOrder: images.length
		})
		const googleImageSource = {
			source: 'Google',
			images
		}
		return googleImageSource;
	};

  yield put(setIsFetchingPostAcqUWImages(true));
  yield put(setPostAcqSelectedOpportunityImages([placeholderSource]));
  try {
    const [googleImageUrls, msrImage] = yield all([
      getGoogleImageUrls(fullAddress(taskData), 640, 360),
      getUWPropertyImage(taskData.asgPropID, null, 'PostAcquisitionTaskUWMain')
    ]);

    let propertyImages = [];
    if (msrImage) {
      propertyImages = [...msrImage]
    }
    propertyImages.push(getGoogleImageSource(googleImageUrls));
    if (propertyImages.length) { // If property has images, add to cache and update selectedOpportunityImages, else leave placeholder.
      yield put(setPostAcqSelectedOpportunityImages(propertyImages));
    }
    yield put(setIsFetchingPostAcqUWImages(false));
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
    yield put(setIsFetchingPostAcqUWImages(false));
  }
}